import {
    BoundingBox,
    Coordinate,
    MapDataItem,
    MapDirectionsSetting,
    MapIsochroneSetting,
} from '@bpd-library/types';
import { Actions, createStore } from '@bpd-library/utilities';

export type SearchResultPlace = {
    country?: string;
    province?: string;
    city?: string;
    district?: string;
};

export type SearchResult = {
    items: MapDataItem[];
    result: any;
    place?: SearchResultPlace;
    originalBoundingBox?: BoundingBox;
    expandedBoundingBox?: BoundingBox;
    kmPadding?: number;
};

interface MapFocusOptions {
    zoom?: boolean;
    static?: boolean;
}

export interface MapLayerRasterSettings {
    layerRaster: string;
    minzoom?: number;
    maxzoom?: number;
}

export type MapManualSearch = string | Coordinate;

export interface MapState {
    data: MapDataItem[];
    mapInstance?: mapboxgl.Map;
    hover: {
        current?: MapDataItem;
        previous?: MapDataItem;
    };
    filter?: MapDataItem[];
    click?: MapDataItem;
    focus: {
        current?: MapDataItem[];
        previous?: MapDataItem[];
        options?: MapFocusOptions;
    };
    searchResult?: SearchResult;
    searchIsMoving: boolean;
    manualSearch?: MapManualSearch;
    layerRasterSettings?: MapLayerRasterSettings;
    isochroneSetting?: MapIsochroneSetting;
    directionsSetting?: MapDirectionsSetting;
    currentZoom?: number;
}

const initialState: MapState = {
    data: [],
    hover: {},
    focus: {},
    searchIsMoving: false,
};

type MapStoreActions =
    | 'updateMapData'
    | 'updateHover'
    | 'clearHover'
    | 'updateClick'
    | 'setFocus'
    | 'clearFocus'
    | 'updateSearchResult'
    | 'clearSearchResult'
    | 'updateSearchIsMoving'
    | 'setFilter'
    | 'clearFilter'
    | 'setMapInstance'
    | 'setLayerRaster'
    | 'manualSearch'
    | 'setIsochrone'
    | 'clearIsochrone'
    | 'setDirections'
    | 'clearDirections'
    | 'setZoom';

const actions: Actions<MapStoreActions, MapState> = {
    updateMapData: (state: MapState, data: MapDataItem[]) => {
        state.data = data;
        return state;
    },
    updateHover: (state: MapState, hover: MapDataItem) => {
        state.hover = {
            current: hover,
            previous: state.hover.current,
        };

        return state;
    },
    clearHover: (state: MapState) => {
        state.hover = {
            current: undefined,
            previous: state.hover.current,
        };

        return state;
    },
    updateClick: (state: MapState, click: MapDataItem) => {
        state.click = { ...click };
        return state;
    },
    setFocus: (state: MapState, { ids, options }: { ids: string[]; options: MapFocusOptions }) => {
        const dataItems = state.data.filter(({ id }) => ids.indexOf(id) > -1);

        if (dataItems.length) {
            state.focus = {
                current: dataItems,
                previous: state.focus?.current,
                options,
            };
        }

        return state;
    },
    clearFocus: (state: MapState) => {
        state.focus = {
            current: undefined,
            previous: state.focus.current,
        };

        return state;
    },
    updateSearchResult: (state: MapState, searchResult: SearchResult) => {
        state.searchResult = searchResult;
        return state;
    },
    clearSearchResult: (state: MapState) => {
        state.searchResult = undefined;
        return state;
    },
    setFilter: (state: MapState, data: MapDataItem[]) => {
        state.filter = data;
        return state;
    },
    clearFilter: (state: MapState) => {
        state.filter = undefined;
        return state;
    },
    setMapInstance: (state: MapState, mapInstance: mapboxgl.Map) => {
        state.mapInstance = mapInstance;
        return state;
    },
    manualSearch: (state: MapState, query: MapManualSearch) => {
        state.manualSearch = query;
        return state;
    },
    updateSearchIsMoving: (state: MapState, searchIsMoving: boolean) => {
        state.searchIsMoving = searchIsMoving;
        return state;
    },
    setLayerRaster: (state: MapState, layerRasterSettings: MapLayerRasterSettings) => {
        state.layerRasterSettings = layerRasterSettings;
        return state;
    },
    setIsochrone: (state: MapState, isochroneSetting: MapIsochroneSetting) => {
        state.isochroneSetting = isochroneSetting;
        return state;
    },
    clearIsochrone: (state: MapState) => {
        state.isochroneSetting = undefined;
        return state;
    },
    setDirections: (state: MapState, directionsSetting: MapDirectionsSetting) => {
        state.directionsSetting = directionsSetting;
        return state;
    },
    clearDirections: (state: MapState) => {
        state.directionsSetting = undefined;
        return state;
    },
    setZoom: (state: MapState, zoom: number) => {
        state.currentZoom = zoom;
        return state;
    },
};

export const mapStore = createStore<MapState, MapStoreActions>({
    initialState,
    actions,
});

/*
    Setters
*/
export const updateMapData = (data: MapDataItem[]) => {
    mapStore.dispatch('updateMapData', data);
};

export const clearHover = () => {
    mapStore.dispatch('clearHover');
};

export const updateHover = (hover: MapDataItem) => {
    mapStore.dispatch('updateHover', hover);
};

export const updateClick = (click: MapDataItem) => {
    mapStore.dispatch('updateClick', click);
};

export const setFocus = (ids: string[], options: MapFocusOptions = {}) => {
    mapStore.dispatch('setFocus', { ids, options });
};

export const clearFocus = () => {
    mapStore.dispatch('clearFocus');
};

export const updateSearchResult = (searchResult: SearchResult) => {
    mapStore.dispatch('updateSearchResult', searchResult);
};

export const clearSearchResult = () => {
    mapStore.dispatch('clearSearchResult');
};

export const setFilter = (data: MapDataItem[]) => {
    mapStore.dispatch('setFilter', data);
};

export const clearFilter = () => {
    mapStore.dispatch('clearFilter');
};

export const setMapInstance = (mapInstance: mapboxgl.Map) => {
    mapStore.dispatch('setMapInstance', mapInstance);
};

export const manualSearch = (query: MapManualSearch) => {
    mapStore.dispatch('manualSearch', query);
};

export const updateSearchIsMoving = (isMoving: boolean) => {
    mapStore.dispatch('updateSearchIsMoving', isMoving);
};

export const setLayerRaster = (layerRasterSettings: MapLayerRasterSettings) => {
    mapStore.dispatch('setLayerRaster', layerRasterSettings);
};

export const setIsochrone = (isochroneSetting: MapIsochroneSetting) => {
    mapStore.dispatch('setIsochrone', isochroneSetting);
};

export const clearIsochrone = () => {
    mapStore.dispatch('clearIsochrone');
};

export const setDirections = (directionsSetting: MapDirectionsSetting) => {
    mapStore.dispatch('setDirections', directionsSetting);
};

export const clearDirections = () => {
    mapStore.dispatch('clearDirections');
};

export const setZoom = (zoom: number) => {
    mapStore.dispatch('setZoom', zoom);
};

/*
    Getters
*/
export const getMapboxData = () => mapStore.getState().data;
export const getHover = () => mapStore.getState().hover;
export const getMapInstance = () => mapStore.getState().mapInstance;
export const getSearchIsMoving = () => mapStore.getState().searchIsMoving;
